import { formatDisplayValue } from '@/constants/common.constant'
import { getSecurityDetails } from '@/services/whatIfAnalysis.service'
import { convertNumericDateToString } from '@/utils/dateUtils'
import { themeClassName } from '@/utils/grid-utils/ag-grid-config'
import { defaultAgGridSidebarProps } from '@/utils/grid-utils/colDefs'
import { validateInput, ValidationPresets } from '@/utils/UserInputValidation'
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model'
import { CellValueChangedEvent, ColDef, GridApi, GridReadyEvent } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import { Search } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// CSS styles for alternating column colors
const columnStyles = `
  .ag-theme-quartz .ag-cell.white-column {
    background-color: #ffffff !important;
  }
  
  .ag-theme-quartz .ag-cell.grey-column {
    background-color: #e6e8ebff !important;
  }
  
  .ag-theme-quartz .ag-row:hover .ag-cell.white-column {
    background-color: #f5f5f5 !important;
  }
  
  .ag-theme-quartz .ag-row:hover .ag-cell.grey-column {
    background-color: #e6e8ebff !important;
  }
  
  .ag-theme-quartz .ag-row-selected .ag-cell.white-column {
    background-color: #e3f2fd !important;
  }
  
  .ag-theme-quartz .ag-row-selected .ag-cell.grey-column {
    background-color: #e6e8ebff !important;
  }
  
  /* Pinned top row styling for add new row */
  .ag-theme-quartz .ag-floating-top {
    border-bottom: 2px solid #dee2e6 !important;
    font-style: italic !important;
  }
  
  .ag-theme-quartz .ag-floating-top .ag-cell {
    color: #6c757d !important;
    font-style: italic !important;
    border-right: 1px solid #dee2e6 !important;
  }
  
  .ag-theme-quartz .ag-floating-top .ag-cell:focus {
    color: #495057 !important;
    font-style: normal !important;
  }
  
  /* Apply alternating column colors to pinned row */
  .ag-theme-quartz .ag-floating-top .ag-cell.white-column {
    background-color: #ffffff !important;
  }
  
  .ag-theme-quartz .ag-floating-top .ag-cell.grey-column {
    background-color: #e6e8ebff !important;
  }
  
  .ag-theme-quartz .ag-floating-top .ag-cell.white-column:focus {
    background-color: #f5f5f5 !important;
  }
  
  .ag-theme-quartz .ag-floating-top .ag-cell.grey-column:focus {
    background-color: #d1d3d6 !important;
  }
  
  /* Center align all column headers */
  .ag-theme-quartz .ag-header-cell-label {
    justify-content: center !important;
  }
  
  .ag-theme-quartz .ag-header-cell-text {
    text-align: center !important;
  }
  
  /* Center align grouped column headers */
  .ag-theme-quartz .ag-header-group-cell-label {
    justify-content: center !important;
  }
  
  .ag-theme-quartz .ag-header-group-text {
    text-align: center !important;
  }
  
  /* Ensure all header content is centered */
  .ag-theme-quartz .ag-header-cell-comp-wrapper {
    justify-content: center !important;
  }
`

interface SelectedPortfolio {
  name: string
  date: string
}

interface SecurityData {
  secid: string
  axsid?: string | number
  price: string | number
  edur: number | null
  qual: string
  holdings_current: number
  holdings_whatif: number
  mkval: string | number
  mkval_current?: number
  pmkval_current: number
  pmkval_whatif: number
  sector: string
  dur_contr_current: number
  dur_contr_whatif: number
  // columns for selection
  description?: string
  country?: string
  currency?: string
  yield?: number | null
  coup?: number | null
  matu?: string
  pdur?: number | null
  mval?: number
  fmval?: number
  currencyCode: number
  baseCurrencyCode: number
  gainloss?: number
}

interface SecurityListsPanelProps {
  selectedPortfolio: SelectedPortfolio | null
  securities?: SecurityData[]
  loading?: boolean
  error?: string | null
  onAddSecurity?: (security: SecurityData) => void
  onUpdateSecurity?: (index: number, updatedSecurity: SecurityData) => void
  onDeleteWarningChange?: (message: string | null) => void
  onNotification?: (message: string) => void
  adjustCashToggle?: boolean
  onAdjustCashToggleChange?: (enabled: boolean) => void
}

// Custom cell renderer for numeric values with formatting
const NumericCellRenderer = (props: any) => {
  const value = props.value
  const field = props.colDef?.field
  
  // Helper function to format numbers with appropriate precision and remove trailing zeros
  const formatNumber = (num: number): string => {
    const absNum = Math.abs(num)
    
    // Handle extremely small or large numbers (scientific notation) - can be positive or negative
    if (absNum < 1e-10 || absNum > 1e10) {
      return '0'
    }
    
    // For holdings columns, use 2 decimal precision
    if (field && (field.includes('holdings') || field === 'holdings_current' || field === 'holdings_whatif')) {
      const formatted = num.toFixed(2)
      return formatted.replace(/\.?0+$/, '')
    }
    
    // For other numeric columns, use original logic
    let decimalPlaces: number
    if (absNum >= 1000) decimalPlaces = 0        // Large numbers: 1000 -> "1000"
    else if (absNum >= 100) decimalPlaces = 1    // 100-999: 123.4 -> "123.4"
    else if (absNum >= 1) decimalPlaces = 2      // 1-99: 12.34 -> "12.34"
    else decimalPlaces = 4                       // Small numbers: max 4 decimal places
    
    // Format with the determined decimal places, but cap at 4 maximum
    const maxDecimalPlaces = Math.min(decimalPlaces, 4)
    const formatted = num.toFixed(maxDecimalPlaces)
    
    // Remove trailing zeros and unnecessary decimal point (only after decimal)
    const finalResult = formatted.includes('.') ? formatted.replace(/\.?0+$/, '') : formatted
    
    return finalResult
  }
  
  if (field && (field.includes('whatif') || field.includes('_whatif'))) {
    if (value === null || value === 'null' || value === undefined) return '--'
    if (value === 0) return ''
    if (typeof value === 'number') {
      if (isNaN(value)) return '' 
      return formatNumber(value)
    }
    // Handle invalid strings from failed validation
    if (typeof value === 'string') {
      const num = Number(value)
      return isNaN(num) ? '' : formatNumber(num)
    }
    return value
  }
  
  // Current columns should show 0
  if (value === null || value === undefined) return '0'
  if (value === 0) return '0'
  if (typeof value === 'number') {
    if (isNaN(value)) return '0' 
    return formatNumber(value)
  }
  // Handle invalid strings from failed validation
  if (typeof value === 'string') {
    const num = Number(value)
    return isNaN(num) ? '0' : formatNumber(num)
  }
  return value
}

const SecurityListsPanel: React.FC<SecurityListsPanelProps> = ({ 
  selectedPortfolio, 
  securities = [], 
  loading = false, 
  error = null,
  onAddSecurity,
  onUpdateSecurity,
  onDeleteWarningChange,
  onNotification,
  adjustCashToggle = false,
  onAdjustCashToggleChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [gridApi, setGridApi] = useState<GridApi | null>(null)
  const gridRef = useRef<any>(null)
  const [isUpdatingPinnedRow, setIsUpdatingPinnedRow] = useState(false)
  const [newlyAddedSecIds, setNewlyAddedSecIds] = useState<Set<string>>(new Set())
  const [unpinnedSecIds, setUnpinnedSecIds] = useState<Set<string>>(new Set())
  const [currentPortfolioId, setCurrentPortfolioId] = useState<string>('')
  const [notificationMessage, setNotificationMessage] = useState<string | null>(null)
  const [selectedRows, setSelectedRows] = useState<SecurityData[]>([])
  const prevSecuritiesRef = useRef<SecurityData[]>([])
  const isInitialLoad = useRef(true)

  useEffect(() => {
    setNewlyAddedSecIds(new Set())
    setUnpinnedSecIds(new Set())
    setCurrentPortfolioId(selectedPortfolio?.name || '')
    prevSecuritiesRef.current = []
    isInitialLoad.current = true 
  }, [selectedPortfolio])
  
  useEffect(() => {
    const prevSecurities = prevSecuritiesRef.current
    
    setNewlyAddedSecIds(prev => {
      const newSet = new Set(prev)
      const currentSecIds = new Set(securities.map(s => s.secid))
      
      // Clean up pinned securities that are no longer in the securities array
      prev.forEach(secid => {
        if (!currentSecIds.has(secid)) {
          newSet.delete(secid)
        }
      })
      
      return newSet
    })
    
    // Only auto-pin new securities if not in initial load
    if (isInitialLoad.current) {
      if (securities.length > 0) {
        isInitialLoad.current = false
      }
    } else if (prevSecurities.length > 0) {
      const newSecurities = securities.filter(current => 
        !prevSecurities.some(prev => prev.secid === current.secid) &&
        ((Number(current.holdings_current) || 0) === 0 && (Number(current.holdings_whatif) || 0) === 0) &&
        !unpinnedSecIds.has(current.secid)
      )
        
      if (newSecurities.length > 0) {
        setNewlyAddedSecIds(prev => {
          const newSet = new Set(prev)
          newSecurities.forEach(security => newSet.add(security.secid))
          return newSet
        })
      }
    } else if (isInitialLoad.current && securities.length > 0) {
      isInitialLoad.current = false
    }
    
    prevSecuritiesRef.current = [...securities]
  }, [securities, unpinnedSecIds])

  const addSecurityToPinned = useCallback((security: SecurityData) => {
    setNewlyAddedSecIds(prev => new Set(prev).add(security.secid))
  }, [])

  // Function to unpin a security (move from pinned to regular)
  const unpinSecurity = useCallback((secid: string) => {
    setNewlyAddedSecIds(prev => {
      const newSet = new Set(prev)
      newSet.delete(secid)
      return newSet
    })
    // Track that this security has been manually unpinned
    setUnpinnedSecIds(prev => {
      const newSet = new Set(prev)
      newSet.add(secid)
      return newSet
    })
  }, [])

  const [gridState, setGridState] = useState<any>(() => {
    // Initialize with saved column state from localStorage
    try {
      const savedColumnState = localStorage.getItem('whatif-column-state')
      if (savedColumnState) {
        const columnState = JSON.parse(savedColumnState)
        return {
          columnState: columnState
        }
      }
    } catch (error) {
      console.warn('Failed to parse saved column state:', error)
    }
    return null
  })
  const [newSecurity, setNewSecurity] = useState<SecurityData>({
    secid: '', 
    price: formatDisplayValue(null), 
    edur: null as number | null, 
    qual: formatDisplayValue(null), 
    holdings_current: 0, 
    holdings_whatif: 0,
    mkval: formatDisplayValue(null), 
    mkval_current: 0,
    pmkval_current: 0, 
    pmkval_whatif: 0, 
    sector: formatDisplayValue(null),
    dur_contr_current: 0, 
    dur_contr_whatif: 0,
    description: formatDisplayValue(null),
    country: formatDisplayValue(null),
    currency: formatDisplayValue(null),
    yield: null,
    coup: null,
    matu: formatDisplayValue(null),
    pdur: null,
    mval: 0,
    fmval: 0,
    currencyCode: 0,
    baseCurrencyCode: 0
  })

  // Grid container ref for keyboard events
  const gridContainerRef = useRef<HTMLDivElement>(null)

  // Handle keyboard shortcuts for selection
  const handleKeyboardSelection = useCallback((event: KeyboardEvent) => {
    if (!gridApi) return
    
    const { key, shiftKey } = event
    
    if (key === 'ArrowDown' && shiftKey) {
      event.preventDefault()
      
      const selectedNodes = gridApi.getSelectedNodes()
      const focusedCell = gridApi.getFocusedCell()
      
      if (selectedNodes.length > 0) {
        // Extend selection down by one row
        const lastSelectedRowIndex = selectedNodes[selectedNodes.length - 1].rowIndex || 0
        
        // Get all data rows
        const allDataRows: any[] = []
        gridApi.forEachNode((node: any) => {
          if (!node.rowPinned && node.data && node.data.secid !== 'Enter CUSIP...') {
            allDataRows.push(node)
          }
        })
        
        // Find the next row to select
        const hasPinnedTop = gridApi.getPinnedTopRowCount() > 0
        let nextRowIndex = lastSelectedRowIndex
        if (hasPinnedTop) {
          nextRowIndex = lastSelectedRowIndex // Don't adjust if extending selection
        }
        
        // Select the next row if it exists
        if (nextRowIndex < allDataRows.length - 1) {
          const nextRow = gridApi.getRowNode((nextRowIndex + 1).toString())
          if (nextRow && !nextRow.rowPinned) {
            nextRow.setSelected(true)
          }
        }
      } else if (focusedCell) {
        // Start selection from focused cell
        const currentRowIndex = focusedCell.rowIndex || 0
        const currentRow = gridApi.getRowNode(currentRowIndex.toString())
        if (currentRow && !currentRow.rowPinned) {
          currentRow.setSelected(true)
        }
      }
      
    } else if (key === 'ArrowUp' && shiftKey) {
      event.preventDefault()
      
      const selectedNodes = gridApi.getSelectedNodes()
      const focusedCell = gridApi.getFocusedCell()
      
      if (selectedNodes.length > 0) {
        // Extend selection up by one row
        const firstSelectedRowIndex = selectedNodes[0].rowIndex || 0
        
        // Select the previous row if it exists and is not pinned
        if (firstSelectedRowIndex > 0) {
          const prevRow = gridApi.getRowNode((firstSelectedRowIndex - 1).toString())
          if (prevRow && !prevRow.rowPinned) {
            prevRow.setSelected(true)
          }
        }
      } else if (focusedCell) {
        // Start selection from focused cell
        const currentRowIndex = focusedCell.rowIndex || 0
        const currentRow = gridApi.getRowNode(currentRowIndex.toString())
        if (currentRow && !currentRow.rowPinned) {
          currentRow.setSelected(true)
        }
      }
      
    } else if (key === 'a' && !shiftKey) {
      // Only select all if not focused on an editable input
      const focusedElement = document.activeElement
      const isEditing = focusedElement && (
        focusedElement.tagName === 'INPUT' ||
        focusedElement.tagName === 'TEXTAREA' ||
        focusedElement.getAttribute('contenteditable') === 'true'
      )
      
      if (!isEditing) {
        event.preventDefault()
        gridApi.selectAll()
      }
    }
  }, [gridApi])

  // Add keyboard event listener for multi-row selection only
  useEffect(() => {
    const container = gridContainerRef.current
    if (container) {
      container.addEventListener('keydown', handleKeyboardSelection)
      return () => {
        container.removeEventListener('keydown', handleKeyboardSelection)
      }
    }
  }, [handleKeyboardSelection])

  // Reset pinned row CUSIP field
  const resetPinnedRowCusip = useCallback(() => {
    if (gridApi) {
      const pinnedRowNode = gridApi.getPinnedTopRow(0)
      if (pinnedRowNode) {
        pinnedRowNode.setDataValue('secid', 'CUSIP')
      }
    }
  }, [gridApi])

  // Clear all What-If values from pinned row after appending
  const clearPinnedRowValues = useCallback(() => {
    if (gridApi) {
      const pinnedRowNode = gridApi.getPinnedTopRow(0)
      if (pinnedRowNode) {
        // Clear all What-If fields
        pinnedRowNode.setDataValue('holdings_whatif', 0)
        pinnedRowNode.setDataValue('pmkval_whatif', 0)
        pinnedRowNode.setDataValue('dur_contr_whatif', 0)
        // Reset CUSIP
        pinnedRowNode.setDataValue('secid', 'CUSIP')
      }
    }
  }, [gridApi])

  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: 'CUSIP',
      field: 'secid',
      width: 150,
      pinned: 'left',
      editable: true,
      cellClass: 'cusip-cell',
      cellEditor: 'agTextCellEditor',
      sortable: true,
      suppressMenu: false,
      onCellClicked: (params) => {
        if (params.data?.isAddRow) {
          // Focus on the cell for editing
          params.api.startEditingCell({
            rowIndex: params.rowIndex!,
            colKey: params.column.getId()
          })
        }
      },
    },
    {
      headerName: 'Quantity',
      children: [
        {
          headerName: 'Current',
          field: 'holdings_current',
          width: 120,
          type: 'numericColumn',
          editable: false,
          cellRenderer: NumericCellRenderer,
          cellClass: 'grey-column',
          cellStyle: { textAlign: 'right' },
        },
        {
          headerName: 'What If',
          field: 'holdings_whatif',
          width: 120,
          type: 'numericColumn',
          editable: true,
          cellRenderer: NumericCellRenderer,
          cellClass: 'editable-cell white-column',
          cellStyle: (params: any) => {
            const value = params.value
            const baseStyle = { textAlign: 'right' }
            if (value > 0) {
              return { ...baseStyle, color: '#4CAF50', fontWeight: 'bold' } // Basic light green for positive
            } else if (value < 0) {
              return { ...baseStyle, color: '#F44336', fontWeight: 'bold' } // Basic red for negative
            }
            return baseStyle
          },
          cellEditor: 'agTextCellEditor',
          cellEditorParams: {
            maxLength: 20
          },
          valueSetter: (params: any): boolean => {
            let value = params.newValue
            
            if (typeof value === 'number' && isNaN(value)) {
              if (onNotification) {
                onNotification('Invalid input: Please enter a valid number')
              }
              return false 
            }
            
            if (value === null || value === undefined || value === '') {
              params.data[params.colDef.field] = 0
              return true
            }
            if (typeof value === 'string') {
              const validation = validateInput(value, ValidationPresets.NUMBER)
              if (validation.isValid && validation.sanitized !== undefined) {
                params.data[params.colDef.field] = validation.sanitized
                return true
              } else {
                // Invalid input - show notification
                if (onNotification) {
                  onNotification(validation.error || 'Invalid input: Please enter a valid number')
                }
                return false
              }
            }
            if (typeof value === 'number') {
              params.data[params.colDef.field] = value
              return true
            }
            return false
          }
        }
      ]
    },
    {
      headerName: '% PORT',
      children: [
        {
          headerName: 'Current',
          field: 'pmkval_current',
          width: 120,
          type: 'numericColumn',
          editable: false,
          valueFormatter: (params: any) => {
            if (params.value === null || params.value === undefined || params.value === '') return ''
            const num = Number(params.value)
            if (Number.isNaN(num)) return ''
            if (num === 0) return '0'
            return num.toFixed(3)
          },
          cellClass: 'grey-column',
          cellStyle: { textAlign: 'right' }
        },
        {
          headerName: 'What If',
          field: 'pmkval_whatif',
          width: 120,
          type: 'numericColumn',
          editable: true,
          valueSetter: (params: any): boolean => {
            let value = params.newValue
            const oldValue = params.oldValue || 0

            if (typeof value === 'number' && isNaN(value)) {
              if (onNotification) {
                onNotification('Invalid input: Please enter a valid number')
              }
              params.data[params.colDef.field] = oldValue
              return true 
            }
            
            if (value === null || value === undefined || value === '') {
              params.data[params.colDef.field] = 0
              return true
            }
            if (typeof value === 'string') {
              const validation = validateInput(value, ValidationPresets.NUMBER)
              if (validation.isValid && validation.sanitized !== undefined) {
                params.data[params.colDef.field] = validation.sanitized
                return true
              } else {
                // Invalid input - show notification
                if (onNotification) {
                  onNotification(validation.error || 'Invalid input: Please enter a valid number')
                }
                params.data[params.colDef.field] = oldValue
                return true 
              }
            }
            if (typeof value === 'number') {
              params.data[params.colDef.field] = value
              return true
            }
            params.data[params.colDef.field] = oldValue
            return true
          },
          valueFormatter: (params: any) => {
            if (params.value === null || params.value === 'null') return '--'
            if (params.value === undefined || params.value === '' || params.value === 0) return ''
            const num = Number(params.value)
            return Number.isNaN(num) ? '' : num.toFixed(3)
          },
          cellClass: 'editable-cell white-column',
          cellStyle: (params: any) => {
            const baseStyle = { textAlign: 'right' as const }
            const value = params.value

            if (value === null || value === undefined || value === '' || value === 0) {
              return baseStyle
            }

            const num = Number(value)
            if (Number.isNaN(num)) return baseStyle

            if (num > 0) {
              return { ...baseStyle, color: '#4CAF50', fontWeight: 'bold' as const }
            }
            if (num < 0) {
              return { ...baseStyle, color: '#F44336', fontWeight: 'bold' as const }
            }
            return baseStyle
          },
          cellEditor: 'agTextCellEditor',
          cellEditorParams: {
            maxLength: 20
          }
        }
      ]
    },
    {
      headerName: 'Contribution Duration',
      children: [
        {
          headerName: 'Current',
          field: 'dur_contr_current',
          width: 120,
          type: 'numericColumn',
          editable: false,
          cellRenderer: NumericCellRenderer,
          cellClass: 'grey-column',
          cellStyle: { textAlign: 'right' }
        },
        {
          headerName: 'What If',
          field: 'dur_contr_whatif',
          width: 120,
          type: 'numericColumn',
          editable: true,
          cellRenderer: NumericCellRenderer,
          cellClass: 'editable-cell white-column',
          cellStyle: (params: any) => {
            const baseStyle = { textAlign: 'right' as const }
            const value = params.value

            if (value === null || value === undefined || value === '' || value === 0) {
              return baseStyle
            }

            const num = Number(value)
            if (Number.isNaN(num)) return baseStyle

            if (num > 0) {
              return { ...baseStyle, color: '#4CAF50', fontWeight: 'bold' as const }
            }
            if (num < 0) {
              return { ...baseStyle, color: '#F44336', fontWeight: 'bold' as const }
            }
            return baseStyle
          },
          cellEditor: 'agTextCellEditor',
          cellEditorParams: {
            maxLength: 20
          },
          valueSetter: (params: any): boolean => {
            let value = params.newValue
          
            if (typeof value === 'number' && isNaN(value)) {
              if (onNotification) {
                onNotification('Invalid input: Please enter a valid number')
              }
              return false 
            }
            
            if (value === null || value === undefined || value === '') {
              params.data[params.colDef.field] = 0
              return true
            }
            if (typeof value === 'string') {
              const validation = validateInput(value, ValidationPresets.NUMBER)
              if (validation.isValid && validation.sanitized !== undefined) {
                params.data[params.colDef.field] = validation.sanitized
                return true
              } else {
                // Invalid input - show notification
                if (onNotification) {
                  onNotification(validation.error || 'Invalid input: Please enter a valid number')
                }
                return false
              }
            }
            if (typeof value === 'number') {
              params.data[params.colDef.field] = value
              return true
            }
            return false
          }
        }
      ]
    },

    {
      headerName: 'Market Value',
      field: 'mkval',
      width: 140,
      editable: false,
      cellRenderer: NumericCellRenderer,
      cellClass: 'grey-column',
      cellStyle: (params: any) => ({ textAlign: 'right' as const })
    },
    {
      headerName: 'Gain/Loss',
      field: 'gainloss',
      width: 140,
      editable: false,
      cellRenderer: NumericCellRenderer,
      cellClass: 'grey-column',
      cellStyle: (params: any) => ({ textAlign: 'right' as const })
    },
    {  
      headerName: 'Price',
      field: 'price',
      width: 120,
      type: 'numericColumn',
      editable: false,
      cellRenderer: NumericCellRenderer,
      cellClass: 'grey-column',
      cellStyle: { textAlign: 'right' }
    },
    {
      headerName: 'Sector',
      field: 'sector',
      width: 100,
      editable: false,
      cellClass: 'grey-column',
      cellStyle: { textAlign: 'right' }
    },
    {
      headerName: 'Rating',
      field: 'qual',
      width: 100,
      editable: false,
      cellClass: 'grey-column',
      cellStyle: { textAlign: 'right' },
    },
    {
      headerName: 'Duration',
      field: 'edur',
      width: 120,
      type: 'numericColumn',
      editable: false,
      cellRenderer: NumericCellRenderer,
      cellClass: 'grey-column',
      cellStyle: { textAlign: 'right' },
    },

    // Optional columns appended at the end, hidden by default
    {
      headerName: 'Description',
      field: 'description',
      width: 260,
      editable: false,
      cellClass: 'grey-column',
      cellStyle: { textAlign: 'left' },
    },
    {
      headerName: 'Country',
      field: 'country',
      width: 110,
      editable: false,
      cellClass: 'grey-column',
      cellStyle: { textAlign: 'right' },
    },
    {
      headerName: 'Currency',
      field: 'currency',
      width: 110,
      editable: false,
      cellClass: 'grey-column',
      cellStyle: { textAlign: 'right' },
    },
    {
      headerName: 'Yield',
      field: 'yield',
      width: 120,
      type: 'numericColumn',
      editable: false,
      cellRenderer: NumericCellRenderer,
      cellClass: 'grey-column',
      cellStyle: { textAlign: 'right' },
    },
    {
      headerName: 'Coupon',
      field: 'coup',
      width: 120,
      type: 'numericColumn',
      editable: false,
      cellRenderer: NumericCellRenderer,
      cellClass: 'grey-column',
      cellStyle: { textAlign: 'right' },
    },
    {
      headerName: 'Maturity Date',
      field: 'matu',
      width: 140,
      editable: false,
      cellClass: 'grey-column',
      cellStyle: { textAlign: 'right' },
      valueFormatter: (params) => convertNumericDateToString(params.value),
    }
  ], [])

  // Handle CUSIP Enter key press
  const handleCusipEnter = useCallback(async (rowData: any) => {
    if (isUpdatingPinnedRow || !rowData.secid || !selectedPortfolio) {
      return
    }
    if (rowData?.secid && rowData.secid !== 'CUSIP' && rowData.secid.trim()) {
      setIsUpdatingPinnedRow(true)
      
      // Track results for notification
      let addedCount = 0
      let duplicateCount = 0
      
      try {
        // Use selected portfolio's date, fallback to current date if not available
        let portfolioDate = ''
        if (selectedPortfolio?.date) {
          // Parse the date and extract only the date part (remove time)
          const dateObj = new Date(selectedPortfolio.date)
          portfolioDate = dateObj.toISOString().slice(0, 10).replace(/-/g, '')
        } else {
          // Fallback to current date in YYYYMMDD format
          portfolioDate = new Date().toISOString().slice(0, 10).replace(/-/g, '')
        }
        
        // Parse comma-separated CUSIPs
        const cusipInput = rowData.secid.trim()
        
        // Validate CUSIP input
        const validation = validateInput(cusipInput, {
          fieldName: 'CUSIP',
          maxLength: 1000, // Allow longer input for multiple CUSIPs
          allowedPattern: /^[a-zA-Z0-9,\s\-_()]+$/, // Allow alphanumeric, commas, spaces, dashes, and basic special chars
          blockedChars: ['&', '%', '#', '@', ';', '"', "'", '\\', '/', '*', '<', '>'], // Removed '-' to allow single and double dashes
          blockedPatterns: ["'", '"', '/*', '../', './', '\\'] // Removed '--' to allow double dashes
        })
        
        if (!validation.isValid) {
          setIsUpdatingPinnedRow(false)
          return
        }
        
        const cusipList = cusipInput.split(',').map((cusip: string) => cusip.trim()).filter((cusip: string) => cusip.length > 0)
        
        // Fetch security details using individual API calls
        if (gridApi) {
          for (const cusip of cusipList) {
            try {
              // First check if security already exists
              const securityExists = securities.some(sec => sec.secid === cusip);
              if (securityExists) {
                const message = `${cusip} Security already exists in portfolio`
                setNotificationMessage(message)
                duplicateCount++
                continue
              }
              // And for the success/other notifications, update them similarly:
              if (duplicateCount > 0 && addedCount > 0) {
                setNotificationMessage(`${addedCount} securities added, ${duplicateCount} already exist in portfolio`)
              } else if (duplicateCount > 0) {
                setNotificationMessage(duplicateCount === 1 
                  ? 'Security already exists in portfolio' 
                  : 'All securities already exist in portfolio')
              }
              
              // Only make API call if security doesn't exist
              const securityResponse = await getSecurityDetails(cusip, portfolioDate)
              const securityDetails = securityResponse
              
              const newSecurity: any = {
                secid: securityDetails.secid,
                axsid: securityDetails.axsid || 0,
                price: securityDetails.price ? securityDetails.price.toFixed(2) : formatDisplayValue(securityDetails.price),
                edur: securityDetails.edur || null,
                qual: formatDisplayValue(securityDetails.qual),
                holdings_current: Number(securityDetails.holdings ?? 0), 
                holdings_whatif: rowData.holdings_whatif || 0,  
                mkval: securityDetails.mkval ? securityDetails.mkval.toFixed(2) : formatDisplayValue(securityDetails.mkval),
                mkval_current: Number(securityDetails.mkval ?? 0),
                pmkval_current: Number(securityDetails.pmkval ?? 0),   
                pmkval_whatif: rowData.pmkval_whatif || 0,    
                sector: formatDisplayValue(securityDetails.sector),
                dur_contr_current: Number(securityDetails.dur_contr ?? 0),
                dur_contr_whatif: rowData.dur_contr_whatif || 0,
                description: formatDisplayValue(securityDetails.description),
                country: formatDisplayValue(securityDetails.country),
                currency: formatDisplayValue(securityDetails.currency),
                yield: securityDetails.yield ?? null,
                coup: securityDetails.coup ?? null,
                matu: securityDetails.matu ? convertNumericDateToString(securityDetails.matu) : formatDisplayValue(null),
                pdur: securityDetails.pdur ?? null,
                mval: Number(securityDetails.mval ?? 0),
                fmval: Number(securityDetails.fmval ?? securityDetails.mval ?? 0),
                currencyCode: securityDetails.currency_code,
                baseCurrencyCode: securityDetails.currency_code,
                gainloss: Number(securityDetails.gainloss ?? 0),
              }
              
              // Check for duplicates before adding CUSIP-inserted security
              const isDuplicate = securities.some(existingSec => existingSec.secid === newSecurity.secid)
              
              if (isDuplicate) {
                duplicateCount++
              } else {
                if (onAddSecurity) {
                  onAddSecurity(newSecurity)
                  addSecurityToPinned(newSecurity)
                  addedCount++
                }
              }
            } catch (error) {
              // Handle individual CUSIP not found - create fallback security
              console.warn(`Security not found for CUSIP: ${cusip}`, error)
              const fallbackSecurity: SecurityData = {
                secid: cusip,
                axsid: 0,
                price: formatDisplayValue(null),
                edur: null,
                qual: formatDisplayValue(null),
                holdings_current: 0,
                holdings_whatif: rowData.holdings_whatif || 0,
                mkval: formatDisplayValue(null),
                mkval_current: 0,
                pmkval_current: 0,
                pmkval_whatif: rowData.pmkval_whatif || 0,
                sector: formatDisplayValue(null),
                dur_contr_current: 0,
                dur_contr_whatif: rowData.dur_contr_whatif || 0,
                description: formatDisplayValue(null),
                country: formatDisplayValue(null),
                currency: formatDisplayValue(null),
                yield: null,
                coup: null,
                matu: formatDisplayValue(null),
                pdur: null,
                mval: 0,
                fmval: 0,
                currencyCode: 0,
                baseCurrencyCode: 0,
                gainloss: 0,
              }
              
              // Check for duplicates before adding fallback security
              const isDuplicate = securities.some(existingSec => existingSec.secid === fallbackSecurity.secid)
              
              if (isDuplicate) {
                duplicateCount++
              } else {
                if (onAddSecurity) {
                  addSecurityToPinned(fallbackSecurity)
                  onAddSecurity(fallbackSecurity)
                  addedCount++
                }
              }
            }
          }
          
          clearPinnedRowValues()
        }
      } catch (error) {
        // Error fetching security details - add fallback securities for all CUSIPs
        const cusipInput = rowData.secid.trim()
        const cusipList = cusipInput.split(',').map((cusip: string) => cusip.trim()).filter((cusip: string) => cusip.length > 0)
        
        for (const cusip of cusipList) {
          const fallbackSecurity: SecurityData = {
            secid: cusip,
            axsid: 0,
            price: formatDisplayValue(null),
            edur: null,
            qual: formatDisplayValue(null),
            holdings_current: 0,
            holdings_whatif: rowData.holdings_whatif || 0,
            mkval: formatDisplayValue(null),
            mkval_current: 0,
            pmkval_current: 0,
            pmkval_whatif: rowData.pmkval_whatif || 0,
            sector: formatDisplayValue(null),
            dur_contr_current: 0,
            dur_contr_whatif: rowData.dur_contr_whatif || 0,
            description: formatDisplayValue(null),
            country: formatDisplayValue(null),
            currency: formatDisplayValue(null),
            yield: null,
            coup: null,
            matu: formatDisplayValue(null),
            pdur: null,
            mval: 0,
            fmval: 0,
            currencyCode: 0,
            baseCurrencyCode: 0,
            gainloss: 0,
          }
          
          // Check for duplicates before adding fallback security
          const isDuplicate = securities.some(existingSec => existingSec.secid === fallbackSecurity.secid)
          
          if (isDuplicate) {
            duplicateCount++
          } else {
            if (onAddSecurity) {
              addSecurityToPinned(fallbackSecurity)
              onAddSecurity(fallbackSecurity)
              addedCount++
            }
          }
        }
        
        clearPinnedRowValues()
      } finally {
        setIsUpdatingPinnedRow(false)
        
        // Show notification similar to batch transfer
        if (onNotification) {
          if (duplicateCount > 0 && addedCount > 0) {
            // Some added, some duplicates
            onNotification(`${addedCount} securities added, ${duplicateCount} already exist in portfolio`)
          } else if (duplicateCount > 0 && addedCount === 0) {
            // All were duplicates
            onNotification(`${duplicateCount === 1 ? 'Security already exists' : 'All securities already exist'} in portfolio`)
          }
          // No notification for successful additions (consistent with batch transfer)
        }
      }
    }
  }, [selectedPortfolio, gridApi, isUpdatingPinnedRow, addSecurityToPinned, clearPinnedRowValues, getSecurityDetails])

  // Add Enter key handler for pinned row (after handleCusipEnter is defined)
  useEffect(() => {
    const container = gridContainerRef.current
    if (container && gridApi) {
      const handleEnterKey = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          const focusedCell = gridApi.getFocusedCell()
          if (focusedCell && focusedCell.rowPinned === 'top') {
            event.preventDefault()
            const pinnedRowNode = gridApi.getPinnedTopRow(0)
            if (pinnedRowNode) {
              handleCusipEnter(pinnedRowNode.data)
            }
          }
        }
      }
      
      container.addEventListener('keydown', handleEnterKey)
      return () => {
        container.removeEventListener('keydown', handleEnterKey)
      }
    }
  }, [gridApi, handleCusipEnter])

  // Default column properties
  const defaultColDef: ColDef = useMemo(() => ({
    sortable: true,
    filter: false,
    resizable: true,
    suppressMenu: false,
    floatingFilter: false,
    suppressKeyboardEvent: (params) => {
      return false
    }
  }), [])

  // CALCULATION DATASET - Single source of truth for all data operations
  const calculationData = securities

  // DISPLAY DATASET - Only for grid display and sorting
  const displayData = useMemo(() => {
    return securities.map(security => ({
      ...security,
      isNewlyAdded: newlyAddedSecIds.has(security.secid)
    }))
  }, [securities, newlyAddedSecIds])

  // PINNING LOGIC - Include empty row for CUSIP input plus newly added securities
  const topPinnedData = useMemo(() => {
    const newlyAddedSecurities = displayData.filter(s => s.isNewlyAdded)
    
    // Always include an empty row at the top for CUSIP input
    const emptyRow = {
      secid: '',
      axsid: 0,
      price: '',
      edur: null,
      qual: '',
      holdings_current: 0,
      holdings_whatif: 0,
      mkval: '',
      mkval_current: 0,
      pmkval_current: 0,
      pmkval_whatif: 0,
      sector: '',
      dur_contr_current: 0,
      dur_contr_whatif: 0,
      description: '',
      country: '',
      currency: '',
      yield: null,
      coup: null,
      matu: '',
      pdur: null,
      mval: 0,
      fmval: 0,
      currencyCode: 0,
      baseCurrencyCode: 0,
      gainloss: 0,
      isNewlyAdded: false,
      isEmpty: true // Flag to identify the empty input row
    }
    
    return [emptyRow, ...newlyAddedSecurities]
  }, [displayData]);

  const regularRowData = useMemo(() => {
    return displayData.filter(s => !s.isNewlyAdded)
  }, [displayData]);

  // 2. Simplify your Grid Options
  const gridOptions = useMemo(() => ({
    pagination: true,
    paginationPageSize: 100,
    singleClickEdit: true,
    stopEditingWhenCellsLoseFocus: true,
    maintainColumnOrder: true,
    getRowId: (params: any) => params.data.secid,
    immutableData: true,
    enableSorting: true,
    sortingOrder: ['asc' as const, 'desc' as const],
    sideBar: defaultAgGridSidebarProps,
    suppressContextMenu: true
  }), []);

  // Handle grid ready
  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api)
    params.api.sizeColumnsToFit()
  }, [displayData])

  // Handle column visibility changes to auto-adjust remaining columns
  const onColumnVisible = useCallback((event: any) => {
    if (gridApi) {
      setTimeout(() => {
        gridApi.sizeColumnsToFit()
      }, 100)
    }
  }, [gridApi])

  // Handle cell value changes - use calculation data for index mapping
  const onCellValueChanged = useCallback((event: CellValueChangedEvent) => {
    if (onUpdateSecurity) {
      const secid = event.data.secid
      const rowIndex = calculationData.findIndex(security => security.secid === secid)
      
      if (rowIndex >= 0) {
        const updatedSecurity = { ...event.data }
        delete updatedSecurity.isNewlyAdded
        onUpdateSecurity(rowIndex, updatedSecurity)
        
        // Unpin security after user has made edits and they get saved
        if (newlyAddedSecIds.has(secid)) {
          setTimeout(() => {
            unpinSecurity(secid)
          }, 1000) 
        }
      }
    }
  }, [onUpdateSecurity, calculationData, newlyAddedSecIds, unpinSecurity])

  // Handle row selection changes
  const onSelectionChanged = useCallback((event: any) => {
    const selectedNodes = event.api.getSelectedNodes()
    const selectedData = selectedNodes
      .map((node: any) => node.data)
      .filter((data: any) => data && data.secid !== 'Enter CUSIP...')
    setSelectedRows(selectedData)
  }, [])

  // Handle CUSIP cell editing stopped (when user finishes editing CUSIP cell)
  const handleCellEditingStopped = useCallback((params: any) => {
    if (params.column?.colId === 'secid' && params.rowPinned === 'top') {
      handleCusipEnter(params.data)
    }
  }, [handleCusipEnter])

  // Handle search with debouncing
  const handleSearch = useCallback((searchValue: string) => {
    setSearchTerm(searchValue)
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', searchValue)
    }
  }, [gridApi])

  return (
    <>
      {/* Inject CSS styles for alternating column colors */}
      <style>{columnStyles}</style>
      
      <Box sx={{ 
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
      {/* Header with Portfolio Name */}
      <Box sx={{ 
        backgroundColor: 'white',
        borderBottom: '1px solid #e0e0e0',
        p: 1.5
      }}>
        {/* Portfolio Name */}
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#18cb21ff',
            fontWeight: 600,
            fontSize: '1.1rem'
          }}
        >
          {selectedPortfolio 
            ? `${selectedPortfolio.name} - ${new Date(selectedPortfolio.date).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}` 
            : 'What-If Analysis'
          }
        </Typography>
      </Box>

      {/* Grid Header with Search */}
      <Box sx={{ 
        backgroundColor: '#f5f5f5',
        borderBottom: '1px solid #e0e0e0',
        p: 1,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1
      }}>
        {/* Adjust Cash Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={adjustCashToggle}
              onChange={(e) => onAdjustCashToggleChange?.(e.target.checked)}
              size="small"
              color="primary"
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Adjust Cash
            </Typography>
          }
          sx={{ 
            ml: 1,
            '& .MuiFormControlLabel-label': {
              color: adjustCashToggle ? 'primary.main' : 'text.secondary'
            }
          }}
        />
        
        {/* Search Field */}
        <TextField
          size="small"
          placeholder="Search securities..."
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ 
            width: 250,
            '& .MuiOutlinedInput-root': {
              height: 32,
              backgroundColor: 'white'
            }
          }}
        />
      </Box>

    {/* Content */}
    <Box sx={{ flex: 1, overflow: 'hidden' }}>
      <Box 
        ref={gridContainerRef}
        sx={{ height: '100%' }} 
        className={themeClassName}
        tabIndex={0}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>Loading securities...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          </Box>
        ) : (
          <AgGridReact
            ref={gridRef}
            rowData={regularRowData}
            pinnedTopRowData={topPinnedData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            gridOptions={gridOptions}
            maintainColumnOrder={true}
            getRowStyle={(params: any) => {
              if (params.data?.isNewlyAdded === true) {
                return { 
                  backgroundColor: '#e3f2fd',
                  fontWeight: 'bold'
                }
              }
              return undefined
            }}
            initialState={gridState}
            onStateUpdated={(params) => {
              setGridState(params.state)
              // Save column state to localStorage when it changes
              const columnState = gridRef.current?.api?.getColumnState()
              if (columnState) {
                try {
                  localStorage.setItem('whatif-column-state', JSON.stringify(columnState))
                } catch (error) {
                  console.warn('Failed to save column state:', error)
                }
              }
            }}
            onGridReady={onGridReady}
            onCellValueChanged={onCellValueChanged}
            onCellEditingStopped={handleCellEditingStopped}
            onSelectionChanged={onSelectionChanged}
            modules={[ClientSideRowModelModule]}
            loading={loading}
            loadingOverlayComponent={() => (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography>Loading securities...</Typography>
              </Box>
            )}
            noRowsOverlayComponent={() => (
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                textAlign: 'center',
                gap: 2,
                p: 3
              }}>
                <Typography variant="h6" color="textSecondary" sx={{ fontWeight: 500 }}>
                  No Securities Available
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 400 }}>
                  {selectedPortfolio 
                    ? `No securities found for portfolio "${selectedPortfolio.name}"`
                    : 'Select a portfolio to load securities or add securities manually'
                  }
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tip: Double-click cells to edit • Use Ctrl+C/V for copy/paste • Enter to navigate
                </Typography>
              </Box>
            )}
          />
        )}
      </Box>
    </Box>

    {/* Status Bar */}
    {selectedPortfolio && securities.length > 0 && (
      <Box sx={{ 
        padding: 1, 
        borderTop: '1px solid #e0e0e0', 
        backgroundColor: '#f9f9f9',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="caption" color="text.secondary">
          Total Securities: {securities.length.toLocaleString()}
          {searchTerm && ` | Filtered results shown`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Tip: Click cells to edit What-If cells • Hit Enter to fetch data BEFORE running analysis
        </Typography>
      </Box>
    )}
    </Box>
    </>
  )
}

export default SecurityListsPanel
